import { cn } from '@/lib/cn'
import { formatChannel } from '@/lib/format'
import type { Channel } from '../types'

// Neutral style for channels without a dedicated design token (wire
// values such as `ach`/`nqr`/`api`, or an absent/unknown channel).
const NEUTRAL_BG = 'bg-muted text-muted-fg ring-border'

const CHANNEL_BG: Record<Channel, string> = {
  pos: 'bg-channel-pos/15 text-channel-pos ring-channel-pos/30',
  nip: 'bg-channel-nip/15 text-channel-nip ring-channel-nip/30',
  rtgs: 'bg-channel-rtgs/15 text-channel-rtgs ring-channel-rtgs/30',
  ussd: 'bg-channel-ussd/15 text-channel-ussd ring-channel-ussd/30',
  mobile_app: 'bg-channel-mobile/15 text-channel-mobile ring-channel-mobile/30',
  internet_banking: 'bg-channel-internet/15 text-channel-internet ring-channel-internet/30',
  agent_banking: 'bg-channel-agent/15 text-channel-agent ring-channel-agent/30',
  atm: 'bg-channel-atm/15 text-channel-atm ring-channel-atm/30',
}

function isKnownChannel(c: string): c is Channel {
  return Object.prototype.hasOwnProperty.call(CHANNEL_BG, c)
}

/**
 * Channel-coloured badge. Each channel gets a distinct color (see design
 * tokens in `index.css`) so analysts can scan a list of mixed-channel
 * transactions at a glance.
 */
export function ChannelBadge({ channel }: { channel: string | null | undefined }) {
  // Alerts may legitimately carry no channel (the backend no longer
  // defaults it); show an explicit "Unknown" rather than a wrong channel.
  const key = typeof channel === 'string' ? channel.trim() : ''
  const label = key === '' ? 'Unknown' : formatChannel(key)
  const bg = isKnownChannel(key) ? CHANNEL_BG[key] : key === 'agent' ? CHANNEL_BG.agent_banking : NEUTRAL_BG
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        bg,
      )}
    >
      {label}
    </span>
  )
}
